import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../Components/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../Components/Card";
import FadeIn from "../Animations/FadeIn";
import { AcceptPublicCourseWaitlistEntry, AddPublicCourseWaitlistEntry, GetAllPublishedCourses, GetCourseAvailability } from "../DataService/CourseService";
import { Course } from "../Types/CourseTypes";
import "./RotSignup.css";

type WaitlistForm = {
  name: string;
  email: string;
};

type BenefitItem = {
  title: string;
  text: string;
};

const rotAreas = [
  "Stress och återhämtning",
  "Nervsystemets betydelse för hälsa",
  "Oro och överprestation",
  "Emotionellt ätande",
  "Självkänsla",
  "Relation till mat och kropp",
  "Gränssättning",
  "Hållbara vanor",
];

const processBenefits: BenefitItem[] = [
  { title: "Tillgång till den digitala hälsoportalen", text: "" },
  { title: "Workbook med fakta och reflektionsövningar", text: "" },
  { title: "Veckoteman och uppgifter", text: "" },
  { title: "Verktyg för nervsystemsreglering", text: "" },
  { title: "En förberedande startmodul", text: "" },
  { title: "Personlig vägledning genom hela processen", text: "" },
];

const whyDifferentItems: BenefitItem[] = [
  {
    title: "Vi arbetar med orsaken",
    text: "Inte bara det som syns på ytan.",
  },
  {
    title: "Du får tid att förändras",
    text: "Mellan våra samtal finns utrymme att landa, prova och integrera.",
  },
  {
    title: "Du gör inte resan ensam",
    text: "Jag följer dig genom hela processen och hjälper dig att se, förstå och ta nästa steg.",
  },
];

const rotFitItems = [
  "vill förstå dig själv på djupet",
  "är trött på att börja om",
  "känner igen dig i återkommande mönster",
  "vill skapa förändring som fungerar även i vardagen",
  "är redo att investera i dig själv och din utveckling",
];

const formatPrice = (value?: number | null) => (typeof value === "number" ? value.toLocaleString("sv-SE") : "");

export const RotSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = React.useState(true);
  const [inviteLoading, setInviteLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [inviteError, setInviteError] = React.useState<string | null>(null);
  const [inviteMessage, setInviteMessage] = React.useState<string | null>(null);
  const [inviteCourseId, setInviteCourseId] = React.useState<number | null>(null);
  const [course, setCourse] = React.useState<Course | null>(null);
  const [availability, setAvailability] = React.useState<{ enrolledUsers: number; reservedUsers?: number; isFull: boolean } | null>(null);
  const [waitlistSaved, setWaitlistSaved] = React.useState(false);
  const [waitlistForm, setWaitlistForm] = React.useState<WaitlistForm>({
    name: "",
    email: "",
  });
  const waitlistRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    setWaitlistForm((prev) => (prev.email ? prev : { ...prev, email: "" }));
  }, []);

  React.useEffect(() => {
    const waitlistToken = searchParams.get("waitlistToken");
    if (!waitlistToken) return;

    let mounted = true;

    const accept = async () => {
      setInviteLoading(true);
      try {
        const entry = await AcceptPublicCourseWaitlistEntry(waitlistToken);
        if (!mounted) return;

        setInviteCourseId(entry.courseId);
        setInviteMessage("Din plats är nu reserverad. Fortsätt till betalningen när du är redo.");
      } catch {
        if (!mounted) return;
        setInviteError("Inbjudningslänken är ogiltig eller har gått ut.");
      } finally {
        if (mounted) setInviteLoading(false);
      }
    };

    void accept();
    return () => {
      mounted = false;
    };
  }, [searchParams]);

  React.useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const courses = await GetAllPublishedCourses();
        const rotCourse = (courses ?? [])
          .filter((item) => {
            const category = String(item.category ?? "").toLowerCase();
            const title = String(item.title ?? "").toLowerCase();
            return category === "__system-rot" || category === "rot" || title.includes("rot");
          })
          .sort((a, b) => {
            const aTime = a.startDate ? new Date(a.startDate).getTime() : Number.MAX_SAFE_INTEGER;
            const bTime = b.startDate ? new Date(b.startDate).getTime() : Number.MAX_SAFE_INTEGER;
            return aTime - bTime;
          })[0] ?? null;

        if (!mounted) return;

        if (!rotCourse) {
          setError("ROT-programmet är inte publicerat ännu.");
          return;
        }

        setCourse(rotCourse);
        const courseAvailability = await GetCourseAvailability(rotCourse.id);
        if (!mounted) return;
        setAvailability(courseAvailability);
      } catch {
        if (!mounted) return;
        setError("Kunde inte hämta ROT-programmet.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, []);

  const handleChange = (field: keyof WaitlistForm, value: string) => {
    setWaitlistForm((prev) => ({ ...prev, [field]: value }));
  };

  const scrollToWaitlist = () => {
    waitlistRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleProceedToPayment = () => {
    if (!course) return;
    if (!inviteCourseId) {
      setError("Du behöver först acceptera din plats via länken i mejlet.");
      return;
    }

    const [firstName, ...rest] = waitlistForm.name.trim().split(/\s+/).filter(Boolean);
    const lastName = rest.join(" ");

    navigate(`/rot/balance/${course.id}`, {
      state: {
        firstName: firstName || "",
        lastName: lastName || "",
        email: waitlistForm.email || "",
        paymentStage: "registration",
      },
    });
  };

  const handleJoinWaitlist = async () => {
    if (!course) return;
    if (!waitlistForm.name.trim() || !waitlistForm.email.trim()) {
      setError("Fyll i namn och e-post för att gå med i väntelistan.");
      return;
    }

    try {
      await AddPublicCourseWaitlistEntry(course.id, {
        name: waitlistForm.name.trim(),
        email: waitlistForm.email.trim(),
      });
      setWaitlistSaved(true);
    } catch (err) {
      console.error("Failed to join waitlist", err);
      setError("Kunde inte lägga dig i väntelistan.");
    }
  };

  return (
    <div className="rot-page">
      <section className="rot-hero">
        <div className="rot-shell rot-hero-grid">
          <FadeIn className="rot-hero-copy">
            <p className="rot-kicker">ROT-programmet</p>
            <h1>För dig som vill förstå varför du gör som du gör – och skapa förändring som håller</h1>
            <p>Det finns många program som talar om vad du ska göra.</p>
            <p>ROT handlar istället om att förstå varför du gör som du gör.</p>
            <p>Istället för fler regler, krav och snabba lösningar arbetar vi med det som ligger bakom dina beteenden och återkommande mönster.</p>
            <p>När vi förstår roten blir förändring inte längre något du måste kämpa fram.</p>
            <p>Det blir något du bygger inifrån.</p>
          </FadeIn>

          <FadeIn className="rot-hero-card">
            <div className="rot-hero-image-wrap">
              <img src="/images/rot.png" alt="ROT" className="rot-hero-image" />
            </div>
            <div className="rot-hero-card-body">
              <p className="rot-hero-card-kicker">Nuvarande kohort</p>
              <h2>{course?.title ?? "ROT-programmet"}</h2>
              <div className="rot-meta-grid">
                <span>Startdatum: {course?.startDate ? new Date(course.startDate).toLocaleDateString("sv-SE") : "Ej satt"}</span>
                <span>Slutbetalning senast: {course?.finalPaymentDeadline ? new Date(course.finalPaymentDeadline).toLocaleDateString("sv-SE") : "Ej satt"}</span>
                <span>Svarstid: {course?.acceptanceResponseDays ? `${course.acceptanceResponseDays} dagar` : "Ej satt"}</span>
                <span>Platser: {course?.seatLimit ?? "Obegränsat"}</span>
              </div>
              {availability && <p>Anmälda + reserverade: {availability.enrolledUsers + (availability.reservedUsers ?? 0)}{course?.seatLimit ? ` / ${course.seatLimit}` : ""}</p>}
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="rot-section">
        <div className="rot-shell rot-narrow">
          <FadeIn className="rot-section-heading">
            <h2>I ROT arbetar vi bland annat med</h2>
          </FadeIn>

          <div className="rot-area-grid">
            {rotAreas.map((item) => (
              <FadeIn key={item}>
                <Card className="rot-area-card">
                  <CardContent>
                    <p>{item}</p>
                  </CardContent>
                </Card>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="rot-section rot-section-muted">
        <div className="rot-shell rot-narrow">
          <FadeIn className="rot-section-heading">
            <h2>Så går det till</h2>
            <p>ROT är en 12 veckors strukturerad förändringsresa där du får tid att förstå, reflektera, prova nya verktyg och integrera det du lär dig i din vardag.</p>
            <p>Du gör inte allt på en gång.</p>
            <p>Mellan våra samtal får du tid att landa, öva och upptäcka vad som faktiskt fungerar för dig.</p>
          </FadeIn>

          <FadeIn>
            <div className="rot-note-stack">
              <p>Du får:</p>
            </div>

            <div className="rot-benefit-list">
              {processBenefits.map((item) => (
                <div key={item.title} className="rot-benefit-item">
                  <p>{item.title}</p>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="rot-section">
        <div className="rot-shell rot-narrow">
          <FadeIn className="rot-section-heading">
            <h2>Vad gör ROT annorlunda?</h2>
          </FadeIn>

          <div className="rot-difference-grid">
            {whyDifferentItems.map((item) => (
              <FadeIn key={item.title}>
                <Card className="rot-difference-card">
                  <CardHeader>
                    <CardTitle>{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p>{item.text}</p>
                  </CardContent>
                </Card>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="rot-section rot-section-muted">
        <div className="rot-shell rot-narrow">
          <FadeIn className="rot-section-heading">
            <h2>ROT är för dig som...</h2>
          </FadeIn>

          <div className="rot-list-grid">
            {rotFitItems.map((item) => (
              <FadeIn key={item}>
                <Card className="rot-list-card">
                  <CardContent>
                    <p>{item}</p>
                  </CardContent>
                </Card>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="rot-section">
        <div className="rot-shell rot-narrow">
          <FadeIn className="rot-section-heading">
            <h2>Begränsat antal platser</h2>
            <p>1–2 starter per år</p>
            <p>ROT öppnar endast en till två gånger per år och jag tar in ett begränsat antal deltagare varje gång.</p>
            <p>Det gör att jag kan erbjuda den personliga vägledning och närvaro som en djupare förändringsprocess kräver.</p>
          </FadeIn>

          <div className="rot-info-row">
            <Card className="rot-info-card">
              <CardContent>
                <p>Startdatum: {course?.startDate ? new Date(course.startDate).toLocaleDateString("sv-SE") : "Ej satt"}</p>
                <p>Platser: {course?.seatLimit ?? "Obegränsat"}</p>
                {availability && <p>Anmälda + reserverade: {availability.enrolledUsers + (availability.reservedUsers ?? 0)}{course?.seatLimit ? ` / ${course.seatLimit}` : ""}</p>}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="rot-section rot-section-muted">
        <div className="rot-shell rot-narrow">
          <FadeIn className="rot-section-heading">
            <h2>Din investering</h2>
            {course?.price != null && <p>ROT – {formatPrice(course.price)} kr</p>}
            <p>Delbetalning möjlig.</p>
          </FadeIn>

          <div className="rot-cta-row">
            <Button text="Anmäl ditt intresse för nästa start" onClick={scrollToWaitlist} />
          </div>
        </div>
      </section>

      <section className="rot-section">
        <div className="rot-shell rot-narrow">
          <FadeIn className="rot-section-heading">
            <h2>Börja redan innan programmet startar</h2>
            <p>Du behöver inte vänta till programstart.</p>
            <p>När du bokat din plats och första startavgiften är betald får du direkt tillgång till en förberedande startmodul.</p>
            <p>Du behöver alltså inte vänta på själva programstarten för att börja din process.</p>
            <p>Det betyder att din förändringsresa kan börja redan nu.</p>
            <p>Du kan börja med att stanna upp, observera och förbereda dig inför de 12 veckorna.</p>
            <p>Du behöver inte bli en ny människa.</p>
            <p>Du behöver förstå den du redan är.</p>
            <p>För när du arbetar med roten kan förändring bli något som håller – inte bara något du börjar om med.</p>
          </FadeIn>
        </div>
      </section>

      <section className="rot-section rot-section-muted">
        <div className="rot-shell rot-narrow">
          <FadeIn className="rot-section-heading">
            <h2>Intresseanmälan till ROT</h2>
          </FadeIn>

          <div className="rot-waitlist-link-row">
            <Button text="Anmäl ditt intresse för nästa start" variant="secondary" onClick={scrollToWaitlist} />
          </div>
        </div>
      </section>

      <section ref={waitlistRef} className="rot-section">
        <div className="rot-shell rot-narrow">
          <div className="rot-waitlist-grid">
            <Card className="rot-waitlist-card">
              <CardHeader>
                <CardTitle>{searchParams.get("waitlistToken") ? "Betala registreringsavgiften" : "Väntelista"}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rot-form-grid">
                  {([ ["name", "Namn"], ["email", "E-post"] ] as const).map(([field, label]) => (
                    <label key={field} className="rot-field">
                      <span>{label}</span>
                      <input
                        value={waitlistForm[field]}
                        onChange={(e) => handleChange(field, e.target.value)}
                        placeholder={label}
                      />
                    </label>
                  ))}
                </div>

                <div className="rot-action-row">
                  <Button text="Tillbaka" variant="secondary" onClick={() => navigate(-1)} />
                  {searchParams.get("waitlistToken") ? (
                    <Button text="Fortsätt till registreringsavgiften" onClick={handleProceedToPayment} />
                  ) : (
                    <Button text={waitlistSaved ? "Tillagd i väntelista" : "Gå med i väntelistan"} variant="secondary" onClick={handleJoinWaitlist} />
                  )}
                </div>
                {!searchParams.get("waitlistToken") && <p>Du ställer dig i kö inför nästa ROT-start. När det närmar sig får du ett mejl om du får en plats.</p>}
                {waitlistSaved && <p>Du är nu anmäld till väntelistan för denna ROT-kohort.</p>}
                {inviteMessage && <p>{inviteMessage}</p>}
              </CardContent>
            </Card>

            <Card className="rot-status-card">
              <CardHeader>
                <CardTitle>{searchParams.get("waitlistToken") ? "Antagningsstatus" : "ROT-programmet"}</CardTitle>
              </CardHeader>
              <CardContent>
                {loading && <p>Laddar ROT-programmet...</p>}
                {inviteLoading && <p>Verifierar din inbjudan...</p>}
                {error && <p className="rot-error">{error}</p>}
                {inviteError && <p className="rot-warning">{inviteError}</p>}
                {inviteMessage && <p className="rot-success">{inviteMessage}</p>}
                {course && (
                  <>
                    <p>{course.title}</p>
                    <p>Startdatum: {course.startDate ? new Date(course.startDate).toLocaleDateString("sv-SE") : "Ej satt"}</p>
                    <p>Slutbetalning senast: {course.finalPaymentDeadline ? new Date(course.finalPaymentDeadline).toLocaleDateString("sv-SE") : "Ej satt"}</p>
                    <p>Svarstid: {course.acceptanceResponseDays ? `${course.acceptanceResponseDays} dagar` : "Ej satt"}</p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};
